/** @jsxImportSource theme-ui @jsxRuntime classic */
import { useLocation } from '@reach/router';
import { Flex, Box, SxStyleProp, Text, Link } from '@tishman/components';
import React, { Fragment, useMemo } from 'react';

import { formatMoney } from '../../../utils';
import { formatDateTime } from '../utils';
import { putInArray } from '../../../services/viva/utils';
import { Flow } from '../../../services/viva/constants';

import type { OrderResponse } from '../../../services/viva/types/response';

interface TicketProps {
  offset?: number;
  order: OrderResponse;
  reservationAk?: string;
}

const LineItem = (props: {
  leftText: string;
  rightText: string | null;
  itemSx?: SxStyleProp;
}): React.JSX.Element => (
  <Flex
    sx={{
      width: '100%',
      justifyContent: 'space-between',
      py: 1,
      ...props.itemSx,
    }}
  >
    <Box sx={{ fontWeight: 500 }}>{props.leftText}</Box>
    <Box sx={{ textAlign: 'right' }}>{props.rightText}</Box>
  </Flex>
);

const itemWithDividerSx: SxStyleProp = {
  'pb': 3,
  'mb': 3,
  'borderBottom': 'solid 1px #777',
  '@media print': {
    pb: '5.5px',
  },
};

export const PrintReceipt = ({
  order,
  reservationAk,
}: TicketProps): React.JSX.Element => {
  const location = useLocation();

  const flow = useMemo(
    () =>
      window.atob(
        new URLSearchParams(location.search)
          .get('Flow')
          ?.replace(/[^\w\d]/, '') ?? '',
      ) as Flow,
    [location.search],
  );

  const ticketDateTime = useMemo(() => {
    const inArray = putInArray(order.ITEMLIST.ITEM);

    if (inArray[0]?.ITEMPERFORMANCELIST?.ITEMPERFORMANCE?.DATETIME) {
      const { abvDate, time } = formatDateTime(
        inArray[0].ITEMPERFORMANCELIST.ITEMPERFORMANCE.DATETIME,
      );

      return { abvDate, time };
    } else if (inArray[0]?.ORDERTICKETLIST) {
      const ticketList = Array.isArray(inArray[0].ORDERTICKETLIST)
        ? inArray[0].ORDERTICKETLIST[0]
        : inArray[0].ORDERTICKETLIST;

      const { abvDate, time } = formatDateTime(
        ticketList.ORDERTICKET.ACTIVEFROM,
      );

      return { abvDate, time };
    }

    return null;
  }, [order]);

  const transactionType = useMemo(
    () => (ticketDateTime?.time ? 'Scheduled Ticket' : 'Unscheduled Ticket'),
    [order],
  );

  return (
    <Box
      id="printTicketBody"
      sx={{
        'p': [2, 4, 6],
        '@media print': {
          pt: 2,
          px: 3,
        },
      }}
    >
      <Text
        sx={{
          paddingBottom: 3,
          borderBottom: 'solid 1px #777',
          marginBottom: 3,
        }}
        variant="mediumTitle"
      >
        Your Receipt
      </Text>
      {reservationAk && (
        <LineItem leftText="Order Number:" rightText={reservationAk} />
      )}
      <LineItem leftText="Transaction:" rightText={transactionType} />
      {ticketDateTime?.abvDate && (
        <LineItem
          itemSx={itemWithDividerSx}
          leftText="Date:"
          rightText={ticketDateTime.abvDate}
        />
      )}
      {flow === Flow.REDEMPTION || flow === Flow.CITY_PASS_REDEMPTION ? (
        <Box sx={itemWithDividerSx} />
      ) : (
        <Fragment>
          {(order.SALE.TOTAL.AMOUNT.NET ?? 0) > 0 && (
            <LineItem
              leftText="Subtotal"
              rightText={formatMoney(order.SALE.TOTAL.AMOUNT.NET ?? 0)}
            />
          )}
          {(order.SALE.TOTAL.AMOUNT.TAX ?? 0) > 0 && (
            <LineItem
              itemSx={itemWithDividerSx}
              leftText="Sales Tax"
              rightText={formatMoney(order.SALE.TOTAL.AMOUNT.TAX ?? 0)}
            />
          )}
          {(order.SALE.TOTAL.AMOUNT.GROSS ?? 0) > 0 && (
            <LineItem
              itemSx={{ fontWeight: 'bold', ...itemWithDividerSx }}
              leftText="Total"
              rightText={formatMoney(order.SALE.TOTAL.AMOUNT.GROSS ?? 0)}
            />
          )}
        </Fragment>
      )}
      <Box
        sx={{
          'mt': 4,
          '@media print': {
            mt: 0,
          },
        }}
      >
        <Text
          sx={{
            'fontSize': 0,
            'marginTop': 5,
            '@media print': {
              fontSize: '10px',
              marginTop: 3,
            },
          }}
        >
          *We kindly ask that you not queue or line up before your specific
          entry time. No entry is allowed before designated times. Please be
          advised the use of tripods is strictly prohibited.
        </Text>
        <Text
          sx={{
            'fontSize': 0,
            'marginTop': 4,
            '@media print': {
              fontSize: '10px',
              marginTop: 3,
            },
          }}
        >
          All purchases made through our online platform are final sale and
          non-refundable, unless otherwise stated. Please see our full{' '}
          <Link href="/documents/RC_TermsAndConditions.pdf" target="_blank">
            Terms & Conditions
          </Link>{' '}
          for more information.
        </Text>
      </Box>
    </Box>
  );
};
